import { fallback } from '@tanstack/zod-adapter';
import { format } from 'date-fns';
import { z } from 'zod';

import { parseApiError } from '@/api/api-axios';
import {
  DEFAULT_INVESTIGATION_METRICS_RANGE,
  INVESTIGATION_METRICS_RANGES,
  type InvestigationMetricsRange,
  type InvestigationMetricsResponse,
  type MetricsPeriod,
  type MetricsWeek,
} from '@/api/types/investigationMetrics';
import { formatDateRange } from '@/utils/dates';
import { formatNumberWithCommas } from '@/utils/numbers';
import { pluralize } from '@/utils/pluralize';
import { searchParam } from '@/utils/router';

const SECONDS_PER_DAY = 86400;
const MS_PER_DAY = SECONDS_PER_DAY * 1000;
const MS_PER_WEEK = 7 * MS_PER_DAY;
const FULL_BASELINE_DAYS = 28;

export const RANGE_LABELS: Record<InvestigationMetricsRange, string> = {
  last_4_weeks: 'Last 4 weeks',
  last_12_weeks: 'Last 12 weeks',
  since_driver_started: 'Since Driver started',
};

const REQUESTED_WEEKS: Record<InvestigationMetricsRange, number | null> = {
  last_4_weeks: 4,
  last_12_weeks: 12,
  since_driver_started: null,
};

export function isInvestigationMetricsRange(value: string): value is InvestigationMetricsRange {
  return INVESTIGATION_METRICS_RANGES.some((range) => range === value);
}

export const investigationMetricsSearchSchema = z.object({
  range: fallback(
    searchParam().pipe(z.enum(INVESTIGATION_METRICS_RANGES)),
    DEFAULT_INVESTIGATION_METRICS_RANGE
  ).default(DEFAULT_INVESTIGATION_METRICS_RANGE),
});

export function formatMetricDuration(seconds: number | null): string {
  if (seconds === null) {
    return '—';
  }
  const hours = Math.round((seconds / 3600) * 10) / 10;
  if (hours < 24) {
    return `${hours.toFixed(1)} hrs`;
  }
  return `${(seconds / SECONDS_PER_DAY).toFixed(1)} days`;
}

export function formatMetricShare(rate: number | null, digits: number): string {
  return rate === null ? '—' : `${(rate * 100).toFixed(digits)}%`;
}

export function formatPerEngineerDay(value: number | null): string {
  return value === null ? '—' : value.toFixed(1);
}

export function formatTicketCount(count: number): string {
  return `${formatNumberWithCommas(count)} ${pluralize(count, 'ticket')}`;
}

export type ChangeBadgeKind = 'faster' | 'pts' | 'more';

export interface ChangeBadgeModel {
  tone: 'good' | 'bad' | 'flat';
  direction: 'up' | 'down' | 'flat';
  text: string;
}

const ABOUT_THE_SAME: ChangeBadgeModel = { tone: 'flat', direction: 'flat', text: 'About the same' };

export function changeBadge(
  kind: ChangeBadgeKind,
  baseline: number | null | undefined,
  current: number | null | undefined
): ChangeBadgeModel | null {
  if (baseline == null || current == null) {
    return null;
  }
  if (kind === 'pts') {
    return pointsBadge((current - baseline) * 100);
  }
  if (baseline === 0) {
    return null;
  }
  return relativeBadge(kind, (current - baseline) / baseline);
}

function pointsBadge(points: number): ChangeBadgeModel {
  if (Math.abs(points) < 0.1) {
    return ABOUT_THE_SAME;
  }
  const lower = points < 0;
  return {
    tone: lower ? 'good' : 'bad',
    direction: lower ? 'down' : 'up',
    text: `${Math.abs(points).toFixed(1)} pts ${lower ? 'lower' : 'higher'}`,
  };
}

function relativeBadge(kind: 'faster' | 'more', change: number): ChangeBadgeModel {
  if (Math.abs(change) < 0.01) {
    return ABOUT_THE_SAME;
  }
  const percent = Math.round(Math.abs(change) * 100);
  const increased = change > 0;
  const better = kind === 'more' ? increased : !increased;
  const word = kind === 'more' ? (increased ? 'more' : 'fewer') : increased ? 'slower' : 'faster';
  return { tone: better ? 'good' : 'bad', direction: increased ? 'up' : 'down', text: `${percent}% ${word}` };
}

export function formatMetricsDate(value: string | Date): string {
  return format(new Date(value), 'MMM d, yyyy');
}

export function formatMetricsGeneratedAt(value: string): string {
  return format(new Date(value), 'MMM d, yyyy, h:mm a');
}

function lastIncludedMoment(end: string): Date {
  return new Date(new Date(end).getTime() - 1);
}

export function formatPeriodDates(period: MetricsPeriod): string {
  return formatDateRange(new Date(period.start), lastIncludedMoment(period.end));
}

export function formatWeekRange(start: string, end: string): string {
  return `${format(new Date(start), 'MMM d')} – ${format(lastIncludedMoment(end), 'MMM d')}`;
}

function periodLengthMs(period: MetricsPeriod): number {
  return new Date(period.end).getTime() - new Date(period.start).getTime();
}

export function periodLengthDays(period: MetricsPeriod): number {
  return Math.round(periodLengthMs(period) / MS_PER_DAY);
}

export function periodLengthWeeks(period: MetricsPeriod): number {
  return Math.round(periodLengthMs(period) / MS_PER_WEEK);
}

export function baselineNote(baseline: MetricsPeriod): string {
  const days = periodLengthDays(baseline);
  const length = `${days} ${pluralize(days, 'day')} before your first investigation`;
  if (days >= FULL_BASELINE_DAYS) {
    return length;
  }
  return `${length} · shorter than 28 days because ticket tracking started ${formatMetricsDate(baseline.start)}`;
}

export function currentNote(response: InvestigationMetricsResponse): string {
  const { current, first_investigation_at: firstInvestigationAt, range } = response;
  if (!current) {
    return '';
  }
  const weeks = periodLengthWeeks(current);
  const requestedWeeks = REQUESTED_WEEKS[range];
  const isShortened = requestedWeeks !== null && weeks < requestedWeeks;
  const isSinceStarted = range === 'since_driver_started';
  let note = `${weeks} ${pluralize(weeks, 'week')}`;
  if (firstInvestigationAt && isShortened) {
    note += ` · shortened from ${requestedWeeks} weeks to the full weeks since your first investigation (${formatMetricsDate(firstInvestigationAt)})`;
  } else if (firstInvestigationAt && isSinceStarted) {
    note += ` · first investigation ${formatMetricsDate(firstInvestigationAt)}`;
  }
  if (firstInvestigationAt && (isShortened || isSinceStarted) && hasUncountedDays(current, firstInvestigationAt)) {
    note += `. The days before ${format(new Date(current.start), 'MMM d')} don't make a full week and aren't counted.`;
  }
  return note;
}

function hasUncountedDays(current: MetricsPeriod, firstInvestigationAt: string): boolean {
  return new Date(current.start).getTime() > new Date(firstInvestigationAt).getTime() + MS_PER_DAY;
}

export type MetricsBannerKind = 'no_investigations' | 'no_current' | 'no_baseline';

export function metricsBanner(response: InvestigationMetricsResponse): MetricsBannerKind | null {
  if (!response.first_investigation_at) {
    return 'no_investigations';
  }
  if (!response.current) {
    return 'no_current';
  }
  if (!response.baseline) {
    return 'no_baseline';
  }
  return null;
}

export function metricsReadyDate(firstInvestigationAt: string): Date {
  return new Date(new Date(firstInvestigationAt).getTime() + MS_PER_WEEK);
}

export interface WeeklyChartPoint {
  weekStart: string;
  weekEnd: string;
  label: string;
  medianDays: number | null;
  p90Days: number | null;
  medianSeconds: number | null;
  p90Seconds: number | null;
  ticketCount: number;
}

export function secondsToDays(seconds: number | null): number | null {
  return seconds === null ? null : seconds / SECONDS_PER_DAY;
}

export function weeklyChartPoints(weekly: MetricsWeek[]): WeeklyChartPoint[] {
  return weekly.map(({ end, start, time_to_resolution: resolution }) => ({
    weekStart: start,
    weekEnd: end,
    label: format(new Date(start), 'MMM d'),
    medianDays: secondsToDays(resolution.median_seconds),
    p90Days: secondsToDays(resolution.p90_seconds),
    medianSeconds: resolution.median_seconds,
    p90Seconds: resolution.p90_seconds,
    ticketCount: resolution.ticket_count,
  }));
}

export function formatDaysTick(days: number): string {
  return `${days}d`;
}

export function chartDayTicks(maxDays: number): number[] {
  const roughTop = Math.max(1, Math.ceil(maxDays * 1.1));
  const step =
    [1, 2, 5, 7, 14, 30, 60, 90, 180].find(
      (candidate) => (candidate > 1 || roughTop <= 5) && roughTop / candidate <= 6
    ) ?? Math.ceil(roughTop / 6 / 180) * 180;
  const top = Math.ceil(roughTop / step) * step;
  const ticks: number[] = [];
  for (let tick = 0; tick <= top; tick += step) {
    ticks.push(tick);
  }
  return ticks;
}

export function chartWeekInterval(weekCount: number, width: number): number {
  const labelCount = Math.max(1, Math.min(8, Math.floor((width - 52) / 72)));
  return Math.max(0, Math.ceil(weekCount / labelCount) - 1);
}

export type MetricsErrorKind = 'timeout' | 'other';

export function metricsErrorKind(error: unknown): MetricsErrorKind {
  return parseApiError(error, '').code === 'investigation_metrics_timeout' ? 'timeout' : 'other';
}
